import { Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';
import { ControllerRenderProps } from 'react-hook-form';
import { RendererType } from '../../data-models/field-definition';
import { ISimpleChoice } from '../../data-models/field2.data-model';
import { PrimitiveType } from '../../data-models/field3.data-model';
import { IFormatterDataModel } from '../../data-models/formatter.data-model';
import { StandardFormatterId } from '../../util/formatter-service';
import { createFormField, IFormField } from '../../view-models/form.view-model';
import { FieldFactory } from '../Form/FieldFactory';
import {
  compareFormatterFamilies,
  getFormatterDisplayName,
  getFormatterExample,
  getFormatterFamily,
  getFormatterHint,
} from './formatter-meta';
import { ConfigurableRendererTypeToType, formatterModelsByTypeAtom } from './formatter-options-state';

export type FormatterId = StandardFormatterId | string;

/** A field's chosen formatting, as the display-config pages store it alongside the field. */
export type FieldFormatConfig = {
  renderer: RendererType; // every field with a configurable formatter should have one
  formatter?: FormatterId; // the customized formatter id
};

export interface IFormatterSelectProps {
  /** Distinguishes this select from others on the page; not part of the stored value. */
  fieldKey: string;
  /** The field's renderer, resolved to the formatter family it can use. */
  renderer?: RendererType;
  /** The family to offer directly, for callers holding a primitive type rather than a renderer. */
  type?: PrimitiveType;
  value: FormatterId | undefined;
  onChange: (formatterId: FormatterId | undefined) => void;
  placeholder?: string;
  disabled?: boolean;
}

export interface IFormatterFormFieldOptions {
  /** The resulting form field's key — a form path for schema-driven forms, synthetic otherwise. */
  key: string;
  /** The field's renderer, resolved to the formatter family it can use. */
  renderer?: RendererType;
  /** The family to offer directly, for callers holding a primitive type rather than a renderer. */
  type?: PrimitiveType;
  /** Current value. Used only to annotate the input with the selected formatter's sample. */
  value: FormatterId | undefined;
  label?: string;
  description?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
}

/**
 * Builds the formatter picker as a plain `IFormField`, so form-driven callers (the KPI and
 * custom-field admin forms) can splice it into their own field lists and have it laid out
 * beside their other fields — while {@link FormatterSelect} renders the same field standalone.
 *
 * Returns `undefined` when the renderer has no configurable formatter family, which is the
 * caller's signal to offer no Format control at all.
 */
export function buildFormatterFormField({
  formatterModelsByType,
  key,
  renderer,
  type,
  value,
  label,
  description,
  placeholder = 'Select a formatter',
  disabled,
  required,
}: IFormatterFormFieldOptions & {
  formatterModelsByType: Map<PrimitiveType, IFormatterDataModel<unknown>[]>;
}): IFormField<unknown> | undefined {
  const resolvedType = type ?? (renderer ? ConfigurableRendererTypeToType[renderer] : undefined);
  if (!resolvedType) {
    return undefined;
  }

  const choices = (formatterModelsByType.get(resolvedType) ?? [])
    .map<ISimpleChoice<string>>((model) => ({
      value: model.id,
      displayName: getFormatterDisplayName(model),
      secondaryLabel: getFormatterExample(model),
      caption: getFormatterHint(model),
      group: getFormatterFamily(model),
    }))
    .sort(
      (a, b) =>
        // MUI renders a fresh heading every time the group changes, so grouped options
        // have to be contiguous — sorting by family before name is what keeps them so.
        compareFormatterFamilies(a.group ?? '', b.group ?? '') ||
        (a.displayName ?? '').localeCompare(b.displayName ?? '')
    );

  // The collapsed field shows only the name, which says what the formatter is called but not
  // what it does to a value. The sample alongside it is the same one the option carried.
  const selectedExample = choices.find((choice) => choice.value === value)?.secondaryLabel;
  const groups = new Set(choices.map((choice) => choice.group).filter(Boolean));

  return createFormField({
    key,
    label,
    description,
    required,
    renderer: RendererType.singleSelect,
    placeholder,
    disabled,
    // A field always resolves to some formatter, so there is no cleared state to offer.
    disableClearable: true,
    rendererMeta: {
      values: choices,
      optionLayout: 'two-line',
      // A single heading over the whole list labels nothing, so short lists stay flat.
      enableGrouping: groups.size > 1,
      inputEndAdornment: selectedExample ? (
        // Capped so a long sample truncates rather than crowding out the name it annotates.
        <Typography variant='caption' color='text.secondary' noWrap maxWidth='60%' title={selectedExample}>
          {selectedExample}
        </Typography>
      ) : undefined,
    },
  });
}

/** Hook form of {@link buildFormatterFormField}, for callers rendering inside React. */
export function useFormatterFormField(options: IFormatterFormFieldOptions): IFormField<unknown> | undefined {
  const formatterModelsByType = useAtomValue(formatterModelsByTypeAtom);
  const { key, renderer, type, value, label, description, placeholder, disabled, required } = options;

  return useMemo(
    () =>
      buildFormatterFormField({
        formatterModelsByType,
        key,
        renderer,
        type,
        value,
        label,
        description,
        placeholder,
        disabled,
        required,
      }),
    [formatterModelsByType, key, renderer, type, value, label, description, placeholder, disabled, required]
  );
}

/**
 * Picks a formatter for a field, and stores the formatter's id.
 *
 * Options are scoped to the family the field's renderer can actually use, named rather than
 * previewed (the preview and the precision hint sit alongside the name), and grouped by
 * sub-family once there is more than one — the number list is long enough that Currency,
 * Percentage, Multiple and Number are worth separating.
 */
export function FormatterSelect({
  fieldKey,
  renderer,
  type,
  value,
  onChange,
  placeholder,
  disabled,
}: IFormatterSelectProps) {
  const formField = useFormatterFormField({
    key: `${fieldKey}-formatConfig`,
    renderer,
    type,
    value,
    placeholder,
    disabled,
  });

  const formProps = useMemo(() => {
    return { value, onChange } as ControllerRenderProps;
  }, [value, onChange]);

  if (!formField) {
    return null;
  }
  return <FieldFactory formField={formField} formProps={formProps} />;
}
